import { useMemo, useState } from 'react';
import AdminLayout from '@/components/Layout/AdminLayout';
import { Button, Card, DataTable, FormSelect, FormCheckbox, TradingStrategySelect } from '@components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { formatTradeLogicV2ExitCandidateLabel } from '@/components/TradeLogic/formatTradeLogicV2ExitCandidateLabel';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { EntityTypeUnion } from '@einhaus/project314-types';
import { Link, useLocation } from 'react-router-dom';

type CombinationRow = {
    combination: {
        exitCandidateId: number | null;
        exitCandidateHash: string | null;
        optionBuyingStrategyIdLeg1: number | null;
        optionBuyingStrategyIdLeg2: number | null;
        optionBuyingStrategyIdLeg3: number | null;
        optionBuyingStrategyIdLeg4: number | null;
        optionBuyingStrategyIdLeg5: number | null;
        optionBuyingStrategyIdLeg6: number | null;
    };
    backtestIds: number[];
    avgProfit: number;
    avgPercentageGain: number;
    avgAvgTradeProfit: number;
    avgWinRate: number;
    avgSharpeRatio: number;
    avgMedianProfitPercent: number;
    avgProfitPerDayPercent: number;
    backtestCount: number;
};

type ExitCandidateRow = {
    exitCandidateId: number | null;
    exitCandidateHash: string | null;
    avgProfit: number;
    avgPercentageGain: number;
    avgWinRate: number;
    avgMedianProfitPercent: number;
    avgProfitPerDayPercent: number;
    backtestCount: number;
};

type OptionStrategyRow = {
    strategyId: number;
    avgProfit: number;
    avgPercentageGain: number;
    avgWinRate: number;
    avgMedianProfitPercent: number;
    avgProfitPerDayPercent: number;
    backtestCount: number;
    legsUsedIn: string[];
};

const entityTypeOptions: { value: '' | EntityTypeUnion['entityType']; label: string }[] = [
    { value: '', label: 'All entity types' },
    { value: 'Asset', label: 'Asset' },
    { value: 'Cryptocurrency', label: 'Cryptocurrency' },
    { value: 'AssetIndex', label: 'Asset Index' }
];

const formatExitCandidate = (candidate: { exitCandidateId: number | null; exitCandidateHash: string | null }) =>
    formatTradeLogicV2ExitCandidateLabel(candidate);

const BacktestParameterAnalysis = () => {
    const location = useLocation();

    const [selectedStrategyId, setSelectedStrategyId] = useState<string>(() => {
        const state = location.state as { strategyId?: number } | null;

        return state?.strategyId ? state.strategyId.toString() : '';
    });

    const [selectedEntityType, setSelectedEntityType] = useState<'' | EntityTypeUnion['entityType']>('');
    const [keyAssetsOnly, setKeyAssetsOnly] = useState(false);

    const strategyIdNumber = selectedStrategyId ? Number(selectedStrategyId) : null;

    const optionBuyingStrategiesQuery = useAppQuery(
        {
            ...trpc.strategy.fetchOptionBuyingStrategies.queryOptions({}),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME,
            enabled: !!strategyIdNumber
        },
        {
            fallbackErrorMessage: 'Failed to fetch option buying strategies',
            showErrorToast: true
        }
    );

    const analysisQuery = useAppQuery(
        {
            ...trpc.backtest.analyzeParameterCombinations.queryOptions(
                strategyIdNumber
                    ? {
                          strategyId: strategyIdNumber,
                          entityType: selectedEntityType || null,
                          keyAssetsOnly,
                          strategyVersionKeyMode: 'current'
                      }
                    : {
                          strategyId: 0,
                          entityType: null,
                          keyAssetsOnly: false,
                          strategyVersionKeyMode: 'current'
                      }
            ),
            enabled: !!strategyIdNumber
        },
        {
            fallbackErrorMessage: 'Failed to analyze backtests',
            showErrorToast: true
        }
    );

    const combinations: CombinationRow[] = analysisQuery.data?.combinations ?? [];
    const exitCandidates: ExitCandidateRow[] = analysisQuery.data?.byExitCandidate ?? [];
    const optionStrategies: OptionStrategyRow[] = analysisQuery.data?.byOptionStrategy ?? [];
    const summary = analysisQuery.data?.summary;
    const notInTop10 = analysisQuery.data?.notInTop10;

    const optionBuyingStrategiesMap = useMemo(() => {
        const map = new Map<number, { id: number; name?: string | null }>();

        optionBuyingStrategiesQuery.data?.strategies?.forEach((strategy) => {
            map.set(strategy.id, { id: strategy.id, name: strategy.name ?? null });
        });

        return map;
    }, [optionBuyingStrategiesQuery.data?.strategies]);

    const formatOptionBuyingStrategy = (strategyId: number) => {
        const strategy = optionBuyingStrategiesMap.get(strategyId);
        const name = strategy?.name?.trim();
        return name ? `${name} (${strategyId})` : `Strategy ${strategyId}`;
    };

    const isLoadingAnalysis = analysisQuery.isLoading && !!strategyIdNumber;

    const combinationColumns: ColumnDef<CombinationRow>[] = useMemo(
        () => [
            {
                id: 'rank',
                header: 'Rank',
                size: 60,
                cell: (info) => info.row.index + 1
            },
            {
                id: 'optionStrategies',
                header: 'Option strategies',
                size: 200,
                cell: (info) => {
                    const combo = info.row.original.combination;

                    const ids = [
                        combo.optionBuyingStrategyIdLeg1,
                        combo.optionBuyingStrategyIdLeg2,
                        combo.optionBuyingStrategyIdLeg3,
                        combo.optionBuyingStrategyIdLeg4,
                        combo.optionBuyingStrategyIdLeg5,
                        combo.optionBuyingStrategyIdLeg6
                    ].filter((id) => id !== null && id !== undefined);

                    if (!ids.length) {
                        return <span className="text-zinc-400 text-xs">None</span>;
                    }

                    return (
                        <div className="text-xs">
                            {ids.map((id) => (
                                <div key={id}>{formatOptionBuyingStrategy(id)}</div>
                            ))}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'backtestCount',
                header: 'Backtests',
                size: 90
            },
            {
                accessorKey: 'avgProfit',
                header: 'Avg profit',
                size: 120,
                cell: (info) => info.row.original.avgProfit.toFixed(2)
            },
            {
                accessorKey: 'avgPercentageGain',
                header: 'Avg % gain',
                size: 120,
                cell: (info) => `${info.row.original.avgPercentageGain.toFixed(2)}%`
            },
            {
                accessorKey: 'avgAvgTradeProfit',
                header: 'Avg trade return %',
                size: 140,
                cell: (info) => `${info.row.original.avgAvgTradeProfit.toFixed(2)}%`
            },
            {
                accessorKey: 'avgWinRate',
                header: 'Avg win rate',
                size: 120,
                cell: (info) => `${info.row.original.avgWinRate.toFixed(2)}%`
            },
            {
                accessorKey: 'avgSharpeRatio',
                header: 'Avg Sharpe',
                size: 110,
                cell: (info) => info.row.original.avgSharpeRatio.toFixed(3)
            },
            {
                accessorKey: 'avgMedianProfitPercent',
                header: 'Avg median %',
                size: 120,
                cell: (info) => `${info.row.original.avgMedianProfitPercent.toFixed(2)}%`
            },
            {
                accessorKey: 'avgProfitPerDayPercent',
                header: 'Avg /day %',
                size: 120,
                cell: (info) => `${info.row.original.avgProfitPerDayPercent.toFixed(2)}%`
            },
            {
                id: 'actions',
                header: 'Actions',
                size: 140,
                cell: (info) => {
                    const row = info.row.original;

                    if (!row.backtestIds.length) {
                        return null;
                    }

                    return (
                        <div className="flex flex-col gap-1 text-xs">
                            <Link className="text-blue-400 hover:underline" to={`/tools/backtestTrades?backtestId=${row.backtestIds[0]}`}>
                                View first backtest
                            </Link>
                            {strategyIdNumber && (
                                <Link
                                    className="text-blue-400 hover:underline"
                                    state={{
                                        strategyId: strategyIdNumber,
                                        entityType: selectedEntityType || null,
                                        optionBuyingStrategyIds: Array.from(
                                            new Set(
                                                [
                                                    row.combination.optionBuyingStrategyIdLeg1,
                                                    row.combination.optionBuyingStrategyIdLeg2,
                                                    row.combination.optionBuyingStrategyIdLeg3,
                                                    row.combination.optionBuyingStrategyIdLeg4,
                                                    row.combination.optionBuyingStrategyIdLeg5,
                                                    row.combination.optionBuyingStrategyIdLeg6
                                                ].filter((id): id is number => id !== null && id !== undefined)
                                            )
                                        ),
                                        exitCandidateIds: row.combination.exitCandidateId ? [row.combination.exitCandidateId] : []
                                    }}
                                    to="/tools/filterBacktests"
                                >
                                    Open in FilterBacktests
                                </Link>
                            )}
                        </div>
                    );
                }
            }
        ],
        [strategyIdNumber, selectedEntityType, optionBuyingStrategiesMap]
    );

    return (
        <AdminLayout containerSize="2000px" title="Backtest Parameter Analysis">
            <div className="mb-4">
                <Card>
                    <div className="p-4">
                        <p className="mb-3 text-sm text-zinc-400">
                            Analysis is scoped to the selected strategy&rsquo;s current behavior version. Previous-version backtests are
                            excluded.
                        </p>
                        <div className="flex flex-wrap items-end gap-4">
                            <div className="min-w-[280px]">
                                <TradingStrategySelect
                                    noMargin
                                    id="strategySelect"
                                    label="Strategy"
                                    value={strategyIdNumber}
                                    onChange={(strategyId) => setSelectedStrategyId(strategyId ? strategyId.toString() : '')}
                                />
                            </div>
                            <div className="min-w-[220px]">
                                <FormSelect
                                    noMargin
                                    id="entityTypeSelect"
                                    label="Entity type"
                                    options={entityTypeOptions}
                                    value={selectedEntityType}
                                    onChange={(event) => setSelectedEntityType(event.target.value as '' | EntityTypeUnion['entityType'])}
                                />
                            </div>
                            <div className="flex items-center">
                                <FormCheckbox
                                    checked={keyAssetsOnly}
                                    id="keyAssetsOnly"
                                    label="Key assets only (SPY, QQQ, etc.)"
                                    onChange={(event) => setKeyAssetsOnly(event.target.checked)}
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    disabled={!strategyIdNumber || isLoadingAnalysis}
                                    isLoading={isLoadingAnalysis}
                                    variant="primary"
                                    onClick={() => analysisQuery.refetch()}
                                >
                                    Analyze
                                </Button>
                                {strategyIdNumber && (
                                    <Link
                                        className="inline-flex items-center px-3 py-2 rounded bg-[#222222] text-xs text-zinc-200 hover:bg-[#333333]"
                                        state={{
                                            strategyId: strategyIdNumber,
                                            entityType: selectedEntityType || null
                                        }}
                                        to="/tools/filterBacktests"
                                    >
                                        Open FilterBacktests
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {strategyIdNumber && isLoadingAnalysis && (
                <Card className="mb-4">
                    <div className="p-4">
                        <DataLoadingSpinner text="Analyzing backtests..." />
                    </div>
                </Card>
            )}

            {strategyIdNumber && analysisQuery.data && (
                <>
                    <div className="mb-4 grid grid-cols-1 xl:grid-cols-3 gap-4">
                        <Card>
                            <div className="p-4">
                                <h3 className="text-lg font-semibold mb-2 text-zinc-100">Summary</h3>
                                {summary ? (
                                    <div className="space-y-1 text-sm text-zinc-200">
                                        <div>
                                            Total backtests: <strong>{summary.totalBacktests}</strong>
                                        </div>
                                        <div>
                                            Unique combinations: <strong>{summary.uniqueCombinations}</strong>
                                        </div>
                                        <div>
                                            Unique exit candidates: <strong>{summary.uniqueExitCandidates}</strong>
                                        </div>
                                        <div>
                                            Unique option strategies: <strong>{summary.uniqueOptionStrategies}</strong>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-sm text-zinc-400">No analysis summary available.</div>
                                )}
                            </div>
                        </Card>

                        <Card>
                            <div className="p-4">
                                <h3 className="text-lg font-semibold mb-2 text-zinc-100">Not in top 10</h3>
                                {notInTop10 ? (
                                    <div className="space-y-2 text-sm text-zinc-200">
                                        <div>
                                            Exit candidates not in top 10:{' '}
                                            {notInTop10.exitCandidates.length ? (
                                                <span className="font-mono text-xs">
                                                    {notInTop10.exitCandidates
                                                        .map((candidate) => formatExitCandidate(candidate))
                                                        .join(', ')}
                                                </span>
                                            ) : (
                                                <span className="text-zinc-400">None</span>
                                            )}
                                        </div>
                                        <div>
                                            Option strategies not in top 10:{' '}
                                            {notInTop10.optionStrategies.length ? (
                                                <span className="font-mono text-xs">
                                                    {notInTop10.optionStrategies.map((id) => formatOptionBuyingStrategy(id)).join(', ')}
                                                </span>
                                            ) : (
                                                <span className="text-zinc-400">None</span>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-sm text-zinc-400">No top 10 differences calculated.</div>
                                )}
                            </div>
                        </Card>

                        <Card>
                            <div className="p-4">
                                <h3 className="text-lg font-semibold mb-2 text-zinc-100">Tips</h3>
                                <div className="text-sm text-zinc-300 space-y-1">
                                    <p>Use the combinations table to identify promising option strategy mixes.</p>
                                    <p>
                                        Cross-reference high-ranking combinations with{' '}
                                        <span className="font-mono">/tools/filterBacktests</span> to inspect individual backtests and
                                        trades.
                                    </p>
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="mb-6">
                        <Card>
                            <div className="p-4">
                                <h3 className="text-lg font-semibold mb-3 text-zinc-100">Parameter combinations (ranked by avg profit)</h3>
                                {combinations.length ? (
                                    <DataTable
                                        striped
                                        columns={combinationColumns}
                                        data={combinations}
                                        pagination={{ enabled: true, pageSize: 20 }}
                                        search={false}
                                    />
                                ) : (
                                    <div className="text-sm text-zinc-400">No valid combinations found for the selected filters.</div>
                                )}
                            </div>
                        </Card>
                    </div>

                    <div className="mb-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <Card>
                            <div className="p-4">
                                <h3 className="text-lg font-semibold mb-3 text-zinc-100">By exit candidate</h3>
                                {exitCandidates.length ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-sm text-zinc-200">
                                            <thead>
                                                <tr className="bg-[#131416]">
                                                    <th className="px-3 py-2 text-left">Candidate</th>
                                                    <th className="px-3 py-2 text-right">Backtests</th>
                                                    <th className="px-3 py-2 text-right">Avg profit</th>
                                                    <th className="px-3 py-2 text-right">Avg % gain</th>
                                                    <th className="px-3 py-2 text-right">Avg win rate</th>
                                                    <th className="px-3 py-2 text-right">Avg median %</th>
                                                    <th className="px-3 py-2 text-right">Avg /day %</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-zinc-800">
                                                {exitCandidates.map((row) => (
                                                    <tr key={`${row.exitCandidateId ?? 'base'}:${row.exitCandidateHash ?? 'none'}`}>
                                                        <td className="px-3 py-2">{formatExitCandidate(row)}</td>
                                                        <td className="px-3 py-2 text-right">{row.backtestCount}</td>
                                                        <td className="px-3 py-2 text-right">{row.avgProfit.toFixed(2)}</td>
                                                        <td className="px-3 py-2 text-right">{row.avgPercentageGain.toFixed(2)}%</td>
                                                        <td className="px-3 py-2 text-right">{row.avgWinRate.toFixed(2)}%</td>
                                                        <td className="px-3 py-2 text-right">{row.avgMedianProfitPercent.toFixed(2)}%</td>
                                                        <td className="px-3 py-2 text-right">{row.avgProfitPerDayPercent.toFixed(2)}%</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-sm text-zinc-400">No exit candidate stats available.</div>
                                )}
                            </div>
                        </Card>

                        <Card>
                            <div className="p-4">
                                <h3 className="text-lg font-semibold mb-3 text-zinc-100">By option buying strategy</h3>
                                {optionStrategies.length ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-sm text-zinc-200">
                                            <thead>
                                                <tr className="bg-[#131416]">
                                                    <th className="px-3 py-2 text-left">Strategy</th>
                                                    <th className="px-3 py-2 text-left">Legs used in</th>
                                                    <th className="px-3 py-2 text-right">Backtests</th>
                                                    <th className="px-3 py-2 text-right">Avg profit</th>
                                                    <th className="px-3 py-2 text-right">Avg % gain</th>
                                                    <th className="px-3 py-2 text-right">Avg win rate</th>
                                                    <th className="px-3 py-2 text-right">Avg median %</th>
                                                    <th className="px-3 py-2 text-right">Avg /day %</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-zinc-800">
                                                {optionStrategies.map((row) => (
                                                    <tr key={row.strategyId}>
                                                        <td className="px-3 py-2">{formatOptionBuyingStrategy(row.strategyId)}</td>
                                                        <td className="px-3 py-2">
                                                            <span className="font-mono text-xs">{row.legsUsedIn.join(', ') || 'N/A'}</span>
                                                        </td>
                                                        <td className="px-3 py-2 text-right">{row.backtestCount}</td>
                                                        <td className="px-3 py-2 text-right">{row.avgProfit.toFixed(2)}</td>
                                                        <td className="px-3 py-2 text-right">{row.avgPercentageGain.toFixed(2)}%</td>
                                                        <td className="px-3 py-2 text-right">{row.avgWinRate.toFixed(2)}%</td>
                                                        <td className="px-3 py-2 text-right">{row.avgMedianProfitPercent.toFixed(2)}%</td>
                                                        <td className="px-3 py-2 text-right">{row.avgProfitPerDayPercent.toFixed(2)}%</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-sm text-zinc-400">No option strategy stats available.</div>
                                )}
                            </div>
                        </Card>
                    </div>
                </>
            )}

            {!strategyIdNumber && (
                <Card>
                    <div className="p-4">
                        <div className="text-sm text-zinc-300">
                            Select a strategy (and optional filters) and click Analyze to see aggregated backtest performance by parameter
                            combination, Trade Logic V2 exit candidate, and option buying strategy.
                        </div>
                    </div>
                </Card>
            )}
        </AdminLayout>
    );
};

export default BacktestParameterAnalysis;
